<template>
	<page-banner
		:widget-data="widgetData"
		@open-modal="openJumpModal"
		@change-tab="updateSelection"
		@reload-page="reloadPage"
		@print-to-pdf="print"
		@download-data="downloadDetailPage"
	>
		<template #title>
			<sticky-text-wrap :text-content="fundNameTitle" />
		</template>
		<template #Iball_shareclass>
				<i-ball-modal tag="ShareClass" />
			</template>
		<template #superscript1>
			<disclosure-superscript
				class="detail-page"
				anchor="holdingsTitle_dropdown"
				:statistics="['TTLHLD']"
			/>
		</template>
		<template #superscript2>
			<disclosure-superscript
				class="detail-page"
				anchor="feesExpenses_dropdown"
				:statistics="['PER']"
			/>
		</template>
	</page-banner>
</template>

<script>
import { mapGetters } from 'vuex';
import PageBanner from '_components_/Shared/PageBanner.vue';
import HTMLtoPDF from '_shared_/utils/print';
import DownloadMixin from '_mixins_/DownloadMixin';
export default {
	name: 'CustomHeader',
	components: {
		PageBanner
	},
	mixins: [DownloadMixin],
	props: ['breadcrumb', 'needsJumpButton', 'needsNavButtons', 'trademark'],
	data() {
		this.audience = this.$store.state.audience;
		this.displayTitle = '';
		return {
			advisor_individual_sections: [
				{ label: 'Summary', elementId: 'summary-section' },
				{ label: 'Returns', elementId: 'returns-section' },
				{ label: 'Ratings & Risk', elementId: 'ratings-section' },
				{ label: 'Holdings', elementId: 'holdings-section' },
				{
					label: 'Prices & Distributions',
					elementId: 'prices-section'
				},
				{ label: 'Fees & Expenses', elementId: 'fees-section' },
				{ label: 'Resources', elementId: 'resources-section' }
			],
			institutional_sections: [
				{ label: 'OverView', elementId: 'summary-section' },
				{ label: 'Returns', elementId: 'returns-section' },
				{
					label: 'Portfolio Composition',
					elementId: 'ratings-section'
				},
				{
					label: 'Prices & Distributions',
					elementId: 'prices-section'
				},
				{ label: 'Fees & Expenses', elementId: 'fees-section' },
				{ label: 'Resources', elementId: 'resources-section' }
			]
		};
	},
	computed: {
		...mapGetters([
			'getAudience',
			'getBaseUrl',
			'getFundInfoData',
			'getFundAttributes',
			'isAFISTargetDate',
			'activeNavTab',
			'fundAvailableShareClasses',
			'isInstitutional'
		]),
		widgetData() {
			return {
				audience: this.getAudience,
				link: this.investmentsLink,
				fundLink: this.fundNameLink,
				fundGroupDescription: this.fundGroupDescription,
				fundSelectionButtonText: this.buttonBranding,
				fundSymbol: this.fundSymbol,
				availableShareClassList: this.fundAvailableShareClasses,
				currentShareClass: this.currentShareClass,
				navSections: this.sections,
				activeNavTab: this.activeNavTab,
				shouldDisplayNavComponents: true,
				fundNameLegal: this.fundNameLegal,
				trademarkLegal: this.trademarkLegal,
				fundNameTitleString: this.fundNameTitleString
			};
		},
		fundNameTitle() {
			const fundName = this.fundNameLegal;
			const trademark = this.trademarkLegal;
			const content = [];
			if (trademark && fundName) {
				//if targetDate objective, the trademark needs to be next to the 'american funds' that is at the beginning of the title
				if (this.isAFISTargetDate) {
					const fundNameList = fundName.split(' ');
					content.push({ content: [fundNameList.shift()] });
					content.push({
						content: [
							`${fundNameList.shift()}<span style="vertical-align: super">${trademark}</span>`
						],
						noWrap: true
					});
					content.push({ content: [fundNameList.join(' ')] });
				} else {
					//package the last word of the name and the trademark into a span
					//This causes the last word and the trademark to wrap together
					const fundNameList = fundName.split(' ');
					const lastWord = fundNameList.pop();
					const name = fundNameList.join(' ');
					content.push({ content: [name] });
					content.push({
						content: [
							`${lastWord}<span style="vertical-align: super;">${trademark}</span>`
						],
						noWrap: true
					});
				}
			} else if (fundName) {
				content.push({ content: [fundName] });
			}
			if (this.fundSymbol) {
				content.push({ content: [` (${this.fundSymbol})`] });
			}
			this.$EventBus.$emit(
				'ExcelData',
				'Title',
				content.reduce((acc, cur) => {
					return `${acc}${cur.content} `;
				}, '')
			);
			return content;
		},
		//this is for target date objectives. formats the title (the "investments/ ..." above the blue bar) so that the trademakr is next to american funds, not at the end
		fundNameTitleString() {
			if (this.isAFISTargetDate) {
				const fundName = this.fundNameLegal;
				const trademark = this.trademarkLegal;
				const fundNameList = fundName.split(' ');
				return ` / ${fundNameList.shift()} ${fundNameList.shift()}<span style="vertical-align: super;">${trademark}</span> ${fundNameList.join(
					' '
				)}`;
			}
			return null;
		},
		investmentsLink() {
			return `${this.getBaseUrl}/${this.getAudience}/investments`;
		},
		fundNameLink() {
			const fundName =
				(this.getFundAttributes && this.getFundAttributes.FundName) ||
				'';
			const shareClass =
				(this.getFundAttributes && this.getFundAttributes.ShareClass) ||
				'';
			const formattedName = this.formatName(fundName, shareClass);
			return `${this.getBaseUrl}/${this.getAudience}/investments/insurance-funds/${formattedName}.htm`;
		},
		fundNameLegal() {
			return (
				(this.getFundAttributes &&
					this.getFundAttributes.FundNameLegal) ||
				''
			);
		},
		fundGroupDescription() {
			return (
				this.getFundAttributes &&
				this.unformatCamelCase(
					this.getFundAttributes.FundGroupDescription
				)
			);
		},
		fundSymbol() {
			return (
				(this.getFundAttributes && this.getFundAttributes.FundSymbol) ||
				''
			);
		},
		buttonBranding() {
			return this.audience === 'advisor' ||
				this.audience === 'institutional'
				? 'JUMP TO A FUND'
				: 'VIEW A DIFFERENT FUND';
		},
		currentShareClass() {
			return (
				(this.getFundAttributes && this.getFundAttributes.ShareClass) || //track this down - update form url
				''
			);
		},
		sections() {
			const sections = this.isInstitutional
				? this.institutional_sections
				: this.advisor_individual_sections;
			return sections;
		},
		trademarkLegal() {
			return (
				(this.getFundInfoData && this.getFundInfoData.trademarkLegal) ||
				''
			);
		}
	},
	watch: {
		sections: {
			immediate: true,
			handler(val) {
				if (val && val[0]) {
					this.$store.commit('changeTab', val[0].label);
				}
			}
		}
	},
	methods: {
		updateSelection(section) {
			this.$store.commit('changeTab', section);
		},
		unformatCamelCase(string) {
			return (
				string &&
				string
					.replace(/([A-Z])/g, ' $1')
					.replace(/^./, str => str.toUpperCase())
			);
		},
		formatName(name, classNumber) {
			name = name.split(' ');
			const result = [];
			for (let i = 0; i < name.length; i++) {
				if (name[i] === '-') break;
				result.push(name[i].toLowerCase());
			}
			return `${result.join('-')}-${classNumber}`;
		},
		reloadPage(shareClass) {
			if (shareClass && shareClass !== this.currentShareClass) {
				if (this.getAudience === 'advisor') {
					localStorage.setItem('advisor-afis-shareclass', shareClass);
				}
				if (this.getAudience === 'individual') {
					localStorage.setItem(
						'individual-afis-shareclass',
						shareClass
					);
				}
				F2.Events.emit(
					'user_preference_selections',
					JSON.stringify({ shareClass, audience: this.getAudience })
				);
				let nextPath = window.location.href.split('-');
				nextPath[nextPath.length - 1] = shareClass.toLowerCase();
				nextPath = nextPath.join('-');
				nextPath += this.integratedURLFormat;
				window.location.assign(nextPath);
			}
		},
		print() {
			HTMLtoPDF.print();
		},
		openJumpModal(type) {
			const modal = { modal: type };
			this.$store.commit('displayModal', modal);
		}
	}
};
</script>
